import { type User, useUser } from '@ad-lucy/gateway-provider';
import { useEffect } from 'react';
import {
  type Location,
  type NavigateFunction,
  useLocation,
  useNavigate,
} from 'react-router-dom';

const sessionItems = {
  ViewsShowroom: [
    'ViewsShowroom::pId',
    'ViewsShowroom::country',
    'ViewsShowroom::view',
    'ViewsShowroom::search',
    'ViewsShowroom::page',
    'ViewsShowroom::limit',
  ],
  ViewsCampaigns: [
    'ViewsCampaignProductPaginationBar::page',
    'ViewsCampaignProductPaginationBar::limit',
    'ViewsCampaigns::pId',
    'ViewsCampaigns::country',
    'ViewsCampaigns::view',
    'ViewsCampaigns::search',
    'ViewsCampaigns::page',
    'ViewsCampaigns::limit',
  ],
  ViewsCampaignsPagination: ['ViewsCampaigns::page', 'ViewsCampaigns::limit'],
  ViewsAccountsActionBar: [
    'ViewsAccountsActionBar::industrySector',
    'ViewsAccountsActionBar::search',
  ],
  ViewsAccountsActionBarPagination: [
    'ViewsAccountsActionBar::page',
    'ViewsAccountsActionBar::limit',
  ],
  ViewsAccountsActionBarModal: [
    'ViewsAccountsActionBarModalAGENCY::search',
    'ViewsAccountsActionBarModalAGENCYGROUP::search',
    'ViewsAccountsActionBarModalRESELLER::search',
    'ViewsAccountsActionBarModalRESELLERGROUP::search',
    'ViewsAccountsActionBarModalCLIENT::search',
    'ViewsAccountsActionBarModalDIRECTCLIENT::search',
    'ViewsContactsActionBarModal::search',
  ],
  ViewsContactsActionBar: [
    'ViewsContactsActionBar::page',
    'ViewsContactsActionBar::limit',
  ],
};

type SessionItemIds = keyof typeof sessionItems;

const removeSessionItems = (itemsId: SessionItemIds) => {
  sessionItems[itemsId].forEach((item) => {
    sessionStorage.removeItem(item);
  });
};

function handleSessionStorage({
  location: { pathname },
  navigate,
  user,
}: {
  location: Location;
  navigate: NavigateFunction;
  user: User;
}) {
  // memorize the last path for redirect after successful login
  if (!pathname.startsWith('/login')) {
    const lastUrl = sessionStorage.getItem('Redirect');

    if (!pathname.includes('/showroom')) {
      removeSessionItems('ViewsShowroom');
    }

    if (!pathname.includes('/campaign')) {
      removeSessionItems('ViewsCampaigns');
    }

    if (
      pathname.startsWith('/campaigns') &&
      lastUrl?.startsWith('/campaigns') &&
      lastUrl !== pathname
    ) {
      removeSessionItems('ViewsCampaignsPagination');
    }
    if (!pathname.includes('/accounts')) {
      removeSessionItems('ViewsAccountsActionBar');
    }
    if (!pathname.includes('/stage/')) {
      removeSessionItems('ViewsAccountsActionBarModal');
    }
    if (
      pathname.startsWith('/crm/contacts') &&
      lastUrl?.startsWith('/crm/contacts') &&
      lastUrl !== pathname
    ) {
      removeSessionItems('ViewsContactsActionBar');
    }
    if (
      pathname.startsWith('/crm/accounts') &&
      lastUrl?.startsWith('/crm/accounts') &&
      lastUrl !== pathname
    ) {
      removeSessionItems('ViewsAccountsActionBarPagination');
    }
  }

  if (pathname.startsWith('/login') || pathname === '/') {
    const redirectTarget =
      (sessionStorage.getItem('Redirect') ?? user.require('quickstart:view'))
        ? '/quickstart'
        : '/campaigns';
    sessionStorage.removeItem('Redirect');
    navigate(redirectTarget);
  }
}

export function SessionHandler({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();

  useEffect(() => {
    localStorage.setItem('Route', location.pathname);
    localStorage.setItem(
      'Location',
      JSON.stringify({ title: 'INIT', pathname: '/' }),
    );

    handleSessionStorage({
      location,
      navigate,
      user,
    });

    return () => {
      localStorage.setItem('Route', location.pathname);
    };
  }, [location, navigate, user]); // ! unsure if this is smart, but ESLint was strongly for it

  return children;
}
